import { create } from "zustand";
import { fetchAssets } from "../api/fetchAssets";
import { fetchAssetGroups } from "../api/fetchAssetGroups";
import { Asset, AssetGroup } from "../models/asset";

const useAssets = create<{
  allGroups: AssetGroup[];
  allAssets: Asset[];
  getAssets: () => Promise<void>;
  getGroups: () => Promise<void>;
  getAssetById: (id: string) => Asset | undefined;
  getFilteredAssets: (
    searchTerm: string,
    limit: number,
    offset: number
  ) => Asset[];
  getAssetIdByChassis: (chassis: string) => string | undefined;
}>((set) => ({
  allGroups: [],
  allAssets: [],
  getAssets: async () => {
    if (useAssets.getState().allAssets.length > 0) {
      return;
    }
    const data = await fetchAssets();
    set({ allAssets: data });
  },
  getGroups: async () => {
    if (useAssets.getState().allGroups.length > 0) {
      return;
    }
    const data = await fetchAssetGroups();
    set({
      allGroups: [
        ...data,
        { id: "field-campains-group", name: "Campanha de campo" },
        { id: "ungruped", name: "Desagrupadas" },
      ],
    });
  },
  getAssetById: (id: string): Asset | undefined => {
    return useAssets
      .getState()
      .allAssets.find((asset: Asset) => asset.id === id);
  },
  getFilteredAssets: (
    searchTerm: string,
    limit: number,
    offset: number
  ): Asset[] => {
    return useAssets
      .getState()
      .allAssets.filter((asset: Asset) => {
        const searchTermLower = (searchTerm?.toString() ?? "").toLowerCase();
        return (
          asset.name?.toLowerCase().includes(searchTermLower) ||
          asset.chassis?.toLowerCase().includes(searchTermLower) ||
          asset.licensePlate?.toLowerCase().includes(searchTermLower)
        );
      })
      .slice(offset, offset + limit);
  },
  getAssetIdByChassis: (chassis: string): string | undefined => {
    const cleanChassis = chassis.trim().toUpperCase();
    return useAssets
      .getState()
      .allAssets.find(
        (asset) => asset.chassis?.trim().toUpperCase() === cleanChassis
      )?.id;
  },
}));

const useRioPlansCache = create<{
  cache: { [key: string]: { data: { [key: string]: string[] }; timestamp: number } };
  getCachedData: (key: string) => { [key: string]: string[] } | null;
  setCachedData: (key: string, data: { [key: string]: string[] }) => void;
  isCacheValid: (key: string) => boolean;
}>((set, get) => ({
  cache: {},
  
  getCachedData: (key: string) => {
    const cached = get().cache[key];
    if (cached && get().isCacheValid(key)) return cached.data;
    return null;
  },
  
  setCachedData: (key: string, data: { [key: string]: string[] }) => {
    set((state) => ({
      cache: {
        ...state.cache,
        [key]: {
          data,
          timestamp: Date.now()
        }
      }
    }));
  },
  
  isCacheValid: (key: string) => {
    const cached = get().cache[key];
    if (!cached) return false;
    
    const cacheAge = Date.now() - cached.timestamp;
    const maxAge = 24 * 60 * 60 * 1000;
    
    return cacheAge < maxAge;
  }
}));

const useVolksTotalCache = create<{
  cache: { [key: string]: { data: [number, string[], string[]]; timestamp: number } };
  getCachedData: (key: string) => [number, string[], string[]] | null;
  setCachedData: (key: string, data: [number, string[], string[]]) => void;
  isCacheValid: (key: string) => boolean;
}>((set, get) => ({
  cache: {},
  
  getCachedData: (key: string) => {
    const cached = get().cache[key];
    if (cached && get().isCacheValid(key)) return cached.data;
    return null;
  },
  
  setCachedData: (key: string, data: [number, string[], string[]]) => {
    set((state) => ({
      cache: {
        ...state.cache,
        [key]: {
          data,
          timestamp: Date.now()
        }
      }
    }));
  },
  
  isCacheValid: (key: string) => {
    const cached = get().cache[key];
    if (!cached) return false;
    
    const cacheAge = Date.now() - cached.timestamp;
    const maxAge = 24 * 60 * 60 * 1000;
    
    return cacheAge < maxAge;
  }
}));

export default useAssets;
export { useRioPlansCache, useVolksTotalCache };
